import React from 'react';

import IconButtonLink from '@dashboard/components/mui-atoms/IconButtonLink';
import { DomainNameApprovalStatus } from '@dashboard/generated/wp/platform';
import useCurrentPlatformId from '@dashboard/hooks/route/useCurrentPlatformId';
import { ArrowBackOutlined, NorthEastOutlined } from '@mui/icons-material';
import { Alert, Divider, Grid, Link, Paper, Skeleton, Stack, Typography } from '@mui/material';

import DkimSection from './DkimSection';
import { useCurrentDomainNameVerificationId, useCurrentDomainNameVerificationQuery } from './hooks';
import MailFromSection from './MailFromSection';
import SendInstructionsDialog from './SendInstructionsDialog';

function LayoutTitle() {
  const { data } = useCurrentDomainNameVerificationQuery();
  const model = data?.DomainNameVerification;

  if (model === undefined) {
    return <Skeleton variant="text" width={200} sx={{ fontSize: '1.5rem' }} />;
  }

  return (
    <Stack spacing={1} direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="h3" component="h1">
        {model.domainName}
      </Typography>
    </Stack>
  );
}

export default function FullLayout() {
  const id = useCurrentDomainNameVerificationId();
  const platformId = useCurrentPlatformId();
  return (
    <Grid container justifyContent="center" sx={{ my: 4 }}>
      <Grid item xs={12} md={10} lg={8} xl={7}>
        <Paper sx={{ p: 4 }}>
          <Stack spacing={4}>
            <Stack spacing={1}>
              <IconButtonLink href={`/o/${platformId}/settings`} edge="start">
                <ArrowBackOutlined />
              </IconButtonLink>
              <Stack>
                <LayoutTitle />
                <Typography color="text.secondary">
                  Authenticate your custom domain to send emails through Waypoint.{' '}
                  <Link
                    href="https://www.usewaypoint.com/docs/verified-domains#step-2-add-dns-records"
                    target="_blank"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 'medium' }}
                  >
                    Learn More
                    <NorthEastOutlined sx={{ fontSize: 16 }} />
                  </Link>
                </Typography>
              </Stack>
            </Stack>

            <StatusAlert />
            <DkimSection />
            <MailFromSection />
            <Divider />
            <SendInstructionsDialog id={id} />
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );
}

function StatusAlert() {
  const { data } = useCurrentDomainNameVerificationQuery();
  const model = data?.DomainNameVerification;

  if (!model) {
    return null;
  }

  switch (model.approvalStatus) {
    case DomainNameApprovalStatus.Pending:
      return (
        <Alert severity="warning">
          <strong>Awaiting DNS verification:</strong> Add the records below to your DNS provider. Changes can take up to
          48 hours to propogate. We'll review and approve your domain automatically once verified.
        </Alert>
      );
    case DomainNameApprovalStatus.Requested:
      return (
        <Alert severity="warning">
          <strong>Pending approval:</strong> your DNS records have now been verified. Our team has been notified and
          will review your domain for approval (typically within 24 hours).
        </Alert>
      );
    case DomainNameApprovalStatus.Approved:
      return (
        <Alert severity="success">
          <strong>Approved:</strong> This domain has been verified and approved. It can now be used to send emails on
          Waypoint using{' '}
          <Link
            target="_blank"
            color="inherit"
            underline="always"
            href="https://www.usewaypoint.com/docs/approved-senders"
          >
            approved senders
          </Link>
          .
        </Alert>
      );
    case DomainNameApprovalStatus.Rejected:
      return (
        <Alert severity="error">
          <strong>Rejected:</strong> This domain was not approved. If you believe this was a mistake, please{' '}
          <Link href="http://www.usewaypoint.com/help" target="_blank" color="inherit" underline="always">
            contact support
          </Link>{' '}
          for a secondary review.
        </Alert>
      );
  }
}
